import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { fetchCustomer, clearCurrentCustomer } from '../features/customers/customersSlice';

export default function CustomerView() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentCustomer, status, error } = useSelector((state) => state.customers);

  useEffect(() => {
    dispatch(fetchCustomer(id));
    return () => {
      dispatch(clearCurrentCustomer());
    };
  }, [dispatch, id]);

  if (status === 'loading') {
    return (
      <div className="container mt-4">
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">{error}</div>
        <Link to="/customers" className="btn btn-secondary">
          Back to Customers
        </Link>
      </div>
    );
  }

  if (!currentCustomer) {
    return null;
  }

  const instance =
    currentCustomer.instance && typeof currentCustomer.instance === 'object'
      ? currentCustomer.instance
      : null;
  const database =
    currentCustomer.database && typeof currentCustomer.database === 'object'
      ? currentCustomer.database
      : null;

  const instanceName = instance?.name || currentCustomer.instance_name || 'N/A';
  const instanceIp = instance?.ip || currentCustomer.instance_ip || 'N/A';
  const instanceCname = instance?.cname || currentCustomer.cname || 'N/A';
  const databaseCluster = database?.cluster || currentCustomer.database_cluster || 'N/A';
  const databaseHost = database?.host || currentCustomer.database_host || 'N/A';
  const databaseName = database?.name || currentCustomer.database_name || 'N/A';

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Customer Details</h2>
        <div>
          {/* <Link to={`/customers/${id}/edit`} className="btn btn-warning me-2">
            Edit
          </Link> */}
          <Link to="/customers" className="btn btn-secondary">
            Back to List
          </Link>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">Basic Information</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <strong>Customer Name:</strong>
              <p className="mb-0">{currentCustomer.customer}</p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Status:</strong>
              <p className="mb-0">
                <span
                  className={`badge bg-${
                    currentCustomer.status === 'active'
                      ? 'success'
                      : 'secondary'
                  }`}
                >
                  {currentCustomer.status}
                </span>
              </p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Migration Category:</strong>
              <p className="mb-0">
                <span
                  className="badge"
                  style={{
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    borderRadius: '0.5rem',
                    padding: '0.35rem 0.75rem'
                  }}
                >
                  {currentCustomer.migration_category || 'babelfish'}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header bg-info text-white">
          <h5 className="mb-0">Domain & Instance Configuration</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <strong>Subdomain:</strong>
              <p className="mb-0">
                <code>{currentCustomer.subdomain}</code>
              </p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>CNAME:</strong>
              <p className="mb-0">
                <code>{instanceCname}</code>
              </p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Instance Name:</strong>
              <p className="mb-0">{instanceName}</p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Instance IP:</strong>
              <p className="mb-0">
                <code>{instanceIp}</code>
              </p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Region:</strong>
              <p className="mb-0">{currentCustomer.region}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header bg-success text-white">
          <h5 className="mb-0">Database Configuration</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <strong>Database Cluster:</strong>
              <p className="mb-0">{databaseCluster}</p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Database Host:</strong>
              <p className="mb-0">
                <code>{databaseHost}</code>
              </p>
            </div>
            <div className="col-md-12 mb-3">
              <strong>Database Name:</strong>
              <p className="mb-0">
                <code>{databaseName}</code>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">Timestamps</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <strong>Created:</strong>
              <p className="mb-0">
                {new Date(currentCustomer.created_at).toLocaleString()}
              </p>
            </div>
            <div className="col-md-6">
              <strong>Last Updated:</strong>
              <p className="mb-0">
                {new Date(currentCustomer.updated_at).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
